import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, ChevronUp, Check, Search, Star } from "lucide-react";
import { Transition } from "@headlessui/react";
import { panelClasses } from "../utils/constants";
import { ProviderIcon } from "../utils/modelIcons";

const DropDownModelSelector = ({
  models = [],
  selectedModel = "",
  onSelect,
  isOpen,
  onToggle,
  closeOthers,
  favorites = [],
  onToggleFavorite,
}) => {
  const searchRef = useRef(null);
  const [search, setSearch] = useState("");
  const [expandedProviders, setExpandedProviders] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (models.length === 1) {
        setExpandedProviders({ [models[0].provider]: true });
      } else {
        setExpandedProviders({});
      }
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [isOpen, models.length]);

  const toggleProvider = (provider) => {
    setExpandedProviders((prev) => ({ ...prev, [provider]: !prev[provider] }));
  };

  const isFav = useCallback(
    (modelKey) => favorites.includes(modelKey),
    [favorites],
  );

  const favoriteModels = useMemo(() => {
    if (favorites.length === 0) return [];
    const result = [];
    for (const group of models) {
      for (const m of group.models) {
        const key = typeof m === "string" ? m : m.model_key;
        if (favorites.includes(key)) {
          result.push({
            ...(typeof m === "string" ? { model_key: m, model_name: m } : m),
            provider: group.provider,
          });
        }
      }
    }
    return result;
  }, [models, favorites]);

  const filteredModels = useMemo(() => {
    if (!search.trim()) return models;
    const q = search.toLowerCase();
    return models
      .map((group) => {
        const filtered = group.models.filter((m) => {
          const name =
            typeof m === "string" ? m : m.model_name || m.model_key || "";
          return (
            name.toLowerCase().includes(q) ||
            group.display_name.toLowerCase().includes(q)
          );
        });
        return filtered.length > 0 ? { ...group, models: filtered } : null;
      })
      .filter(Boolean);
  }, [models, search]);

  const isSearching = search.trim().length > 0;

  const displayLabel = (() => {
    if (!selectedModel) return "Select Model";
    for (const group of models) {
      for (const m of group.models || []) {
        const key = typeof m === "string" ? m : m.model_key;
        if (key === selectedModel) {
          return typeof m === "string"
            ? m.includes("/")
              ? m.split("/").pop()
              : m
            : m.model_name;
        }
      }
    }
    return selectedModel.includes("/")
      ? selectedModel.split("/").pop()
      : selectedModel;
  })();

  const selectedProvider = (() => {
    if (!selectedModel) return "";
    if (selectedModel.includes("/")) return selectedModel.split("/")[0];
    for (const group of models) {
      for (const m of group.models || []) {
        const key = typeof m === "string" ? m : m.model_key;
        if (key === selectedModel) return group.provider;
      }
    }
    return "";
  })();

  const ModelRow = ({ model, provider }) => {
    const modelKey = typeof model === "string" ? model : model.model_key;
    const modelName =
      typeof model === "string"
        ? model.includes("/")
          ? model.split("/").pop()
          : model
        : model.model_name;
    const isSelected = modelKey === selectedModel;
    const favorited = isFav(modelKey);

    return (
      <button
        onClick={() => {
          onSelect(modelKey);
          onToggle();
        }}
        className={[
          "group flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] font-light transition-colors cursor-pointer",
          isSelected ? "bg-velBlue/10" : "hover:bg-btnHover",
        ].join(" ")}
      >
        <ProviderIcon provider={provider} size={15} className="shrink-0" />
        <span
          className={[
            "flex-1 truncate",
            isSelected ? "text-velBlue" : "text-mainText",
          ].join(" ")}
        >
          {modelName}
        </span>
        {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-velBlue" />}
        {onToggleFavorite && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(modelKey);
            }}
            className={[
              "shrink-0 transition-colors cursor-pointer",
              favorited
                ? "text-sunColor"
                : "text-mutedText/30 opacity-0 group-hover:opacity-100",
            ].join(" ")}
          >
            <Star
              className="h-3.5 w-3.5"
              fill={favorited ? "currentColor" : "none"}
              strokeWidth={2}
            />
          </span>
        )}
      </button>
    );
  };

  return (
    <div>
      <button
        onClick={() => {
          closeOthers?.();
          onToggle();
        }}
        className="inline-flex items-center gap-1.5 py-1 text-mutedText hover:text-mainText transition-colors cursor-pointer"
        title="Select model"
      >
        {selectedProvider && (
          <ProviderIcon
            provider={selectedProvider}
            size={14}
            className="shrink-0"
          />
        )}
        <span className="text-[13px] font-light truncate max-w-[160px]">
          {displayLabel}
        </span>
        <ChevronDown className="h-3 w-3 flex-shrink-0" />
      </button>

      <Transition
        show={isOpen}
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="absolute left-0 right-0 bottom-full z-[70]">
          <div className={panelClasses}>
            <div className="max-h-[50vh] overflow-auto">
              {favoriteModels.length > 0 && !isSearching && (
                <div className="border-b border-dropdownDivider">
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 text-[11px] font-light uppercase tracking-wider text-sunColor">
                    <Star
                      className="h-3 w-3 shrink-0"
                      fill="currentColor"
                      strokeWidth={0}
                    />
                    <span>Favorites</span>
                  </div>
                  {favoriteModels.map((m) => (
                    <ModelRow
                      key={m.model_key}
                      model={m}
                      provider={m.provider}
                    />
                  ))}
                </div>
              )}

              {filteredModels.length === 0 ? (
                <div className="px-3 py-6 text-center text-[13px] font-light text-mutedText">
                  {search
                    ? "No models matching search"
                    : "No public models enabled"}
                </div>
              ) : (
                filteredModels.map((group) => {
                  const isExpanded =
                    isSearching || !!expandedProviders[group.provider];

                  return (
                    <div
                      key={group.provider}
                      className="border-b border-dropdownDivider last:border-b-0"
                    >
                      <button
                        onClick={() => toggleProvider(group.provider)}
                        className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-btnHover transition-colors cursor-pointer"
                      >
                        <ProviderIcon
                          provider={group.provider}
                          size={15}
                          className="shrink-0"
                        />
                        <span className="flex-1 text-[12px] font-light text-mutedText">
                          {group.display_name}
                        </span>
                        <span className="text-[11px] font-light text-mutedText/60 tabular-nums">
                          {group.models.length}
                        </span>
                        {isExpanded ? (
                          <ChevronUp
                            className="h-3 w-3 shrink-0 text-mutedText"
                            strokeWidth={2}
                          />
                        ) : (
                          <ChevronDown
                            className="h-3 w-3 shrink-0 text-mutedText"
                            strokeWidth={2}
                          />
                        )}
                      </button>

                      <div
                        className={[
                          "overflow-hidden transition-all duration-200 ease-in-out",
                          isExpanded
                            ? "max-h-[2000px] opacity-100"
                            : "max-h-0 opacity-0",
                        ].join(" ")}
                      >
                        {group.models.map((model) => (
                          <ModelRow
                            key={
                              typeof model === "string"
                                ? model
                                : model.model_key
                            }
                            model={model}
                            provider={group.provider}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-dropdownDivider px-3 py-2">
              <div className="flex items-center gap-2 rounded-lg bg-transparent border border-inputBorder px-2.5 py-1.5">
                <Search className="h-3.5 w-3.5 text-mutedText shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search models..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-[13px] font-light text-mainText placeholder:text-mutedText outline-none"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      searchRef.current?.focus();
                    }}
                    className="text-mutedText hover:text-mainText transition-colors cursor-pointer"
                  >
                    <span className="text-[11px]">✕</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default DropDownModelSelector;
